import React, { useEffect, useState } from "react";
// import React, { useEffect, useState, useContext } from "react";
import { Admin, Resource, CustomRoutes } from "react-admin";
import dataProvider, { updateSubscriptionStatus } from "./components/dataProvider";
import { CategoryList, CategoryEdit, CategoryCreate } from "./components/category";
import { UserList } from "./components/userList";
import AuthProvider from "./authProvider";
import Dashboard from "./Dashboard";
import polyglotI18nProvider from "ra-i18n-polyglot";
import spanishMessages from "@blackbox-vision/ra-language-spanish";
import profile from "./components/profile";
import { Route } from "react-router-dom";
import MyLayout from "./MyLayout";
import { EmployeeList, EmployeeCreate, EmployeeEdit } from "./components/employee";
import { ProductList, ProductCreate, ProductEdit } from "./components/product";
import { NotificationList, NotificationCreate } from "./components/notification";
import { BalanceDeliveryList } from "./components/deliveryBalance";
import { CommissionList, CommissionCreate, CommissionEdit } from "./components/commission";
import Login from "./Login";
import Register from "./Register";
import { createTheme } from "@mui/material/styles";
import { AvailabilityEdit } from "./components/availability";
import { DeliverySettingList, DeliverySettingCreate, DeliverySettingEdit } from "./components/deliverySetting";
import ForgotPasswordPage from "./ForgotPassword";
import ResetPasswordPage from "./ResetPassword";

import { DeliveryList, DeliveryEdit } from "./components/delivery";
import { SOSCreate, SOSEdit, SOSList } from "./components/sos";
import { SaleList, SaleShow } from "./components/sale";
import { ServiceCreate, ServiceEdit, ServiceList } from "./Service";
import { BookingList, BookingShow, BookingCreate } from "./Booking";
import { BalanceList } from "./Booking/Balance";
import { BalanceList as SaleBalanceList } from "./Sale/Balance";
import { AdminBookingBalance, AdminBookingEdit, AdminBookingList } from "./Admin/Booking";
import { EnterpriseCustomerCreate, EnterpriseCustomerEdit, EnterpriseCustomerList } from "./Enterprise/Customer";
import { SaleCreate } from "./Sale";
import { ScheduleList } from "./Schedule/Schedule";
import { UnavailabilityCreate, UnavailabilityEdit, UnavailabilityList } from "./Unavailability";
import { Tutorial } from "./Tutorial/Tutorial";
import { BranchCreate, BranchEdit, BranchList, BranchShow } from "./Enterprise/Branch";
import { CouponCreate, CouponEdit, CouponList } from "./Coupon";
import { PackageList } from "./Package";
import GroupsIcon from "@mui/icons-material/Groups";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import GroupIcon from "@mui/icons-material/Group";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import BusinessIcon from "@mui/icons-material/Business";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BackupTableIcon from "@mui/icons-material/BackupTable";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import AirtablePage from "./AirtablePage";
import { FilterProvider } from "./Booking/FilterContext";
import CustomerServicesPage from "./Enterprise/Customer/components/ServicePage";
import { PackageDetailsPage } from "./Package";
import { UserEdit } from "./components/userEdit";
import { ProviderContext } from "./ProviderContext";
import { NotificationProvider } from "./NotificationContext";
import { TourProvider } from "./contexts/TourContext";
import { OnboardingContext } from "./contexts/OnboardingContext";
import FormsList from "./Forms/FormsList";
import CustomerFormsPage from "./Enterprise/Customer/components/CustomerFormsPage";
import FormResponseBuilder from "./Forms/components/Response/FormResponseBuilder";
import { OnboardingProvider } from "./contexts/OnboardingContext";
import { useContext } from "react";
import TrialFloatingButton from "./components/TrialFloatingButton";
import ForcedPlanModal from "./components/ForcedPlanModal";

const i18nProvider = polyglotI18nProvider(() => spanishMessages, "es");

const muiTheme = createTheme({
  palette: {
    primary: { main: "#FF770D", menuIcon: "#757575" },
    secondary: { main: "#252526" },
  },
});

const App = () => {
  window.hashTalkSmart = "9e0c1de1-6e40-4008-95be-d9f4afb7d08a";

  const scriptUrlTalkSmart = "https://toolhero.desarrollamelo.com/iframe/js/script.tuchatinteligente.min.js";
  const randomVersionTalkSmart = Math.random().toString(36).substring(2); // Generate a random version string

  const newScriptUrlTalkSmart = scriptUrlTalkSmart + "?v=" + randomVersionTalkSmart;

  const scriptElementTalkSmart = document.createElement("script");
  scriptElementTalkSmart.src = newScriptUrlTalkSmart;
  scriptElementTalkSmart.setAttribute("hash", "9e0c1de1-6e40-4008-95be-d9f4afb7d08a");
  document.head.appendChild(scriptElementTalkSmart);
  const [userData, setUserData] = useState(null);
  const [, setLoading] = useState(true); // opcional
  const [, setError] = useState(null); // opcional
  const [provider, setProvider] = useState(null);
  const { setOnboardingSteps } = useContext(OnboardingContext);
  const isFremium = provider?.plan === "freemium";
  const isEnterprise = provider?.plan === "enterprise";
  const canCreate =
    provider?.plan === "franquicia" || provider?.plan === "enterprise" || provider?.branchAdmins.length === 0;
  // Manejar selección de plan cuando el trial expira
  const handlePlanSelection = async (_planKey) => {
    try {
      // Aquí puedes agregar la lógica para procesar la selección del plan
      // Por ejemplo, redirigir a página de pago, actualizar el estado, etc.
      // Ejemplo: redirigir a página de pago o procesamiento
      // window.location.href = `/payment?plan=${planKey}`;
    } catch (error) {
      // Error handling
    }
  };

  // Función para actualizar status cuando el trial expira
  const updateSubscriptionStatusToPending = async (userId) => {
    try {
      // Usar la función del dataProvider
      const updatedUser = await updateSubscriptionStatus({
        userId: userId,
        status: "PENDING",
      });
      setProvider(updatedUser.data); // Actualizar estado directamente
    } catch (error) {
      // Error handling silencioso
    }
  };

  // Verificar si el trial ha expirado
  const checkTrialExpiration = (userSubscription) => {
    if (!userSubscription?.subscription) return false;
    const { next_payment_date } = userSubscription.subscription;

    if (!next_payment_date) return false;

    // Verificar si el trial ha expirado (simple comparación de fechas)
    const currentDate = new Date();
    const endDate = new Date(next_payment_date);
    const timeDiff = endDate.getTime() - currentDate.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return daysDiff <= 0;
  };
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const profile = JSON.parse(localStorage.getItem("profile") || "{}");
        const token = localStorage.getItem("token");

        if (!profile._id || !token) {
          throw new Error("Falta ID de usuario o token");
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}/profile/${profile._id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        setUserData(data.data);
        if (data?.data.role === "admin-sucursal") {
          const responseProvider = await fetch(`${process.env.REACT_APP_API_URL}/profile/${data.data.provider._id}`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });
          if (!responseProvider.ok) {
            throw new Error(`Error ${responseProvider.status}: ${responseProvider.statusText}`);
          }
          const dataProvider = await responseProvider.json();
          setProvider(dataProvider.data);
        } else {
          setProvider(data.data);
        }

        // Verificar si el trial ha expirado y actualizar status si es necesario (solo para proveedores)
        if (checkTrialExpiration(data?.data.role === "admin-sucursal" ? data.data.provider : data.data)) {
          updateSubscriptionStatusToPending(
            data?.data.role === "admin-sucursal" ? data.data.provider._id : data.data._id
          );
        }

        // Sincroniza los pasos de onboarding con el contexto global
        if (data.data.onboardingSteps) {
          setOnboardingSteps(data.data.onboardingSteps);
        }
      } catch (err) {
        // Error silencioso al cargar perfil
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [setOnboardingSteps]);
  const hasPermission = (key) => {
    if (userData?.role !== "admin-sucursal") return true;
    return userData?.permissions?.Configuraciones?.[key] || userData?.permissions?.Herramientas?.[key] || false;
  };
  return (
    <OnboardingProvider initialSteps={userData?.onboardingSteps || []}>
      <TourProvider>
        <ProviderContext.Provider value={{ provider }}>
          <NotificationProvider>
            <Admin
              i18nProvider={i18nProvider}
              authProvider={AuthProvider}
              dashboard={Dashboard}
              dataProvider={dataProvider}
              loginPage={Login}
              theme={muiTheme}
              layout={MyLayout}
            >
              <CustomRoutes noLayout>
                <Route path="/register" element={<Register />} />,
              </CustomRoutes>
              <CustomRoutes noLayout>
                <Route path="/forgotPassword" element={<ForgotPasswordPage />} />,
              </CustomRoutes>
              <CustomRoutes noLayout>
                <Route path="/resetPassword" element={<ResetPasswordPage />} />,
              </CustomRoutes>
              <CustomRoutes>
                <Route key="my-profile" path="/my-profile" element={<profile.edit />} />
              </CustomRoutes>
              <CustomRoutes>
                <Route key="update-password" path="/update-password" element={<profile.password />} />
              </CustomRoutes>
              {(permissions) => {
                return [
                  permissions === "admin" && (
                    <Resource
                      name="categories"
                      list={CategoryList}
                      edit={CategoryEdit}
                      create={CategoryCreate}
                      label="Categorías"
                      options={{ label: "Categorías" }}
                    />
                  ),
                  permissions === "admin" && (
                    <Resource name="providers" list={UserList} edit={UserEdit} options={{ label: "Proveedores" }} />
                  ),
                  permissions === "admin" && (
                    <Resource
                      name="usersEnable"
                      list={UserList}
                      edit={UserEdit}
                      options={{ label: "Habilitar Proveedores" }}
                    />
                  ),
                  (permissions === "admin" || permissions === "marketing") && (
                    <Resource
                      name="notification"
                      list={NotificationList}
                      create={NotificationCreate}
                      options={{ label: "Notificaciones" }}
                    />
                  ),
                  permissions === "admin" && (
                    <Resource
                      name="admin/booking"
                      list={AdminBookingList}
                      edit={AdminBookingEdit}
                      options={{ label: "Reservas" }}
                    />
                  ),
                  permissions === "admin" && (
                    <Resource
                      name="adminBookingBalance"
                      list={AdminBookingBalance}
                      edit={AdminBookingEdit}
                      options={{ label: "Balance de Servicios" }}
                    />
                  ),
                  permissions === "admin" && (
                    <Resource
                      name="commission"
                      list={CommissionList}
                      create={CommissionCreate}
                      edit={CommissionEdit}
                      options={{ label: "Comisiones" }}
                    />
                  ),
                  permissions === "admin" && (
                    <Resource
                      name="deliverysetting"
                      list={DeliverySettingList}
                      edit={DeliverySettingEdit}
                      create={DeliverySettingCreate}
                      options={{ label: "Ajustes de Envíos" }}
                    />
                  ),
                  permissions === "admin" && (
                    <Resource name="delivery" list={DeliveryList} edit={DeliveryEdit} options={{ label: "Envíos" }} />
                  ),
                  permissions === "admin" && (
                    <Resource
                      name="deliveryBalance"
                      list={BalanceDeliveryList}
                      options={{ label: "Balance de Envíos" }}
                    />
                  ),
                  permissions === "admin" && (
                    <Resource
                      name="sos"
                      list={SOSList}
                      edit={SOSEdit}
                      create={SOSCreate}
                      label="SOS"
                      options={{ label: "SOS" }}
                    />
                  ),

                  (permissions === "proveedor-empresa" ||
                    (permissions === "admin-sucursal" && hasPermission("Servicios"))) && (
                    <Resource
                      name="service"
                      list={ServiceList}
                      edit={ServiceEdit}
                      create={permissions === "proveedor-empresa" ? ServiceCreate : null}
                      options={{ label: "Servicios", parent: "settings" }}
                      icon={LocalMallIcon}
                    />
                  ),

                  permissions === "proveedor-empresa" && (
                    <Resource
                      name="enterprise/branches"
                      list={BranchList}
                      edit={BranchEdit}
                      create={canCreate ? BranchCreate : null}
                      options={{ label: "Sucursales", parent: "settings" }}
                      icon={BusinessIcon}
                    />
                  ),

                  (permissions === "proveedor-empresa" ||
                    (permissions === "admin-sucursal" && hasPermission("Disponibilidad"))) && (
                    <Resource
                      name="availabilityWeb"
                      list={AvailabilityEdit}
                      options={{ label: "Disponibilidad", parent: "settings" }}
                      icon={EventAvailableIcon}
                    />
                  ),

                  permissions !== "marketing" && !(permissions === "admin-sucursal" && !hasPermission("Empleados")) && (
                    <Resource
                      name="employee"
                      list={EmployeeList}
                      edit={EmployeeEdit}
                      create={EmployeeCreate}
                      options={{ label: "Empleados", parent: "settings" }}
                      icon={GroupIcon}
                    />
                  ),

                  (permissions === "proveedor-empresa" ||
                    permissions === "proveedor-producto" ||
                    (permissions === "admin-sucursal" && hasPermission("Clientes"))) && (
                    <Resource
                      name="enterprise/customers"
                      list={EnterpriseCustomerList}
                      create={isFremium ? null : EnterpriseCustomerCreate}
                      edit={isFremium ? null : EnterpriseCustomerEdit}
                      options={{ label: "Clientes", parent: "tools" }}
                      icon={GroupsIcon}
                    />
                  ),

                  permissions === "proveedor-empresa" && <Resource name="profile" />,
                  (permissions === "proveedor-empresa" ||
                    (permissions === "admin-sucursal" && hasPermission("Reservas"))) && (
                    <Resource
                      rowClick="show"
                      name="booking"
                      list={BookingList}
                      show={BookingShow}
                      edit={BookingShow}
                      create={BookingCreate}
                      options={{ label: "Reservas", parent: "tools" }}
                      icon={BackupTableIcon}
                    />
                  ),
                  (permissions === "proveedor-empresa" || permissions === "admin-sucursal") && (
                    <Resource
                      rowClick="show"
                      name="package"
                      list={PackageList}
                      options={{ label: "Paquetes", parent: "tools" }}
                      icon={Inventory2OutlinedIcon}
                    />
                  ),
                  (permissions === "proveedor-empresa" ||
                    (permissions === "admin-sucursal" && hasPermission("Balance"))) && (
                    <Resource
                      name="bookingBalance"
                      list={(props) => (
                        <FilterProvider>
                          <BalanceList {...props} />
                        </FilterProvider>
                      )}
                      options={{ label: "Balance", parent: "tools" }}
                      icon={MonetizationOnIcon}
                    />
                  ),
                  permissions === "proveedor-empresa" && <Resource name="updatePassword" />,
                  permissions === "proveedor-producto" && (
                    <Resource
                      name="product"
                      list={ProductList}
                      edit={ProductEdit}
                      create={ProductCreate}
                      options={{ label: "Productos" }}
                    />
                  ),
                  (permissions === "proveedor-producto" || permissions === "admin") && (
                    <Resource
                      rowClick="show"
                      name="sale"
                      list={SaleList}
                      show={SaleShow}
                      create={SaleCreate}
                      options={{ label: "Ventas" }}
                    />
                  ),
                  (permissions === "proveedor-producto" || permissions === "admin") && (
                    <Resource name="sale/balance" list={SaleBalanceList} options={{ label: "Balance de Ventas" }} />
                  ),
                  (permissions === "proveedor-empresa" ||
                    (permissions === "admin-sucursal" && hasPermission("Calendario"))) && (
                    <Resource
                      name="schedule"
                      list={ScheduleList}
                      options={{ label: "Calendario", parent: "tools" }}
                      icon={CalendarMonthIcon}
                    />
                  ),
                  (permissions === "proveedor-empresa" ||
                    (permissions === "admin-sucursal" && hasPermission("HorarioNoDisponible"))) && (
                    <Resource
                      name="unavailability"
                      list={UnavailabilityList}
                      edit={UnavailabilityEdit}
                      create={UnavailabilityCreate}
                      options={{ label: "Horario No Disponible", parent: "tools" }}
                      icon={EventBusyIcon}
                    />
                  ),
                  (permissions === "proveedor-empresa" ||
                    (permissions === "admin-sucursal" && hasPermission("Cupones"))) && (
                    <Resource
                      name="coupon"
                      list={CouponList}
                      edit={!isEnterprise ? null : CouponEdit}
                      create={!isEnterprise ? null : CouponCreate}
                      options={{ label: "Cupones", parent: "tools" }}
                      icon={ConfirmationNumberIcon}
                    />
                  ),
                  <Resource name="register" />,
                  <Resource name="forgotPassword" />,
                  <Resource name="resetPassword" />,
                  <Resource name="availability" />,
                ];
              }}
              <CustomRoutes>
                <Route path="/tutorial" element={<Tutorial />} />
                <Route path="/airtable" element={<AirtablePage />} />
                <Route path="/branch/:id" element={<BranchShow />} />
                <Route path="/enterprise/customers/:id/services" element={<CustomerServicesPage />} />
                <Route path="/package/:id/details" element={<PackageDetailsPage />} />
                <Route path="/enterprise/customers/:id/forms" element={<CustomerFormsPage />} />
                <Route path="/enterprise/customers/:id/forms/crear" element={<FormResponseBuilder mode="create" />} />
                <Route
                  path="/enterprise/customers/:id/forms/ver/:formResponseId"
                  element={<FormResponseBuilder mode="view" />}
                />
                <Route
                  path="/enterprise/customers/:id/forms/editar/:formResponseId"
                  element={<FormResponseBuilder mode="edit" />}
                />
                <Route path="/enterprise/customers/forms" element={<FormsList />} />
              </CustomRoutes>
            </Admin>
            {provider?.subscription?.status === "FREE_TRIAL" && <TrialFloatingButton userSubscription={provider} />}
            <ForcedPlanModal
              open={provider?.subscription?.status === "PENDING" || provider?.subscription?.status === "CANCELLED"}
              userSubscription={provider}
              onSelectPlan={handlePlanSelection}
            />
          </NotificationProvider>
        </ProviderContext.Provider>
      </TourProvider>
    </OnboardingProvider>
  );
};
export default App;
